/**
 * Shared rendering surface for article-detail-shaped routes (the generic
 * `ArticleDetailPage` and the bespoke `EduhousenVerkkokoulutusRoute` /
 * `VapaaAjanTapaturmavakuutusRoute` per TA20.x).
 *
 * Public-facing entry point
 * =========================
 *
 * This module is the public-facing entry point that route consumers
 * import from — `import { DetailSurfaceShell, DetailSurfaceSkeleton,
 * DetailSurfaceNotFound, PolicyStatusCard, … } from
 * './articleDetailSurface.js'`. The implementation is split across
 * three cohesive helper modules, each owning a single concern:
 *
 *   - `detailSurfaceShell.tsx` — the back-header sibling + body
 *     wrapping element shared by all three surface states
 *     (populated/skeleton/not-found). The cross-state composition
 *     contract.
 *   - `detailSurfacePageClasses.ts` — the shared root-element class
 *     string. Lives in its own tiny module so the shell can import
 *     it without a circular surface ↔ shell dependency.
 *   - `detailSurfaceBackHeader.tsx` — the fixed back-header bar
 *     (proto-parity sub-header). Owns the padding-stack rationale,
 *     the in-app-history detection, and the focus / a11y disciplines.
 *
 * The split is intentional separation of concerns rather than an
 * LOC-driven workaround: each helper has a different audience (the
 * shell composes layout; pageClasses is a token; the back-header is
 * a chrome component) and a different change cadence. Re-exporting
 * the helpers' types + functions from this module lets consumers
 * see one cohesive surface without leaking the helper geometry.
 *
 * Hero block lives in `./detailSurfaceHero.tsx` as the single render
 * seam — see that module for the markup / a11y / CLS rationale.
 *
 * Why this module exists
 * ======================
 *
 * Three article-detail routes share byte-identical Tailwind class tokens
 * (`pageClasses`, `headingClasses`, `bodyClasses`,
 * the five `skeleton*Classes`), the same skeleton paragraph count
 * (`SKELETON_PARAGRAPH_COUNT`), and the same skeleton render shape.
 * Lifting them here:
 *
 *   - Locks visual + a11y consistency across every article-detail surface
 *     (one `aria-busy` skeleton shape, one set of token strings). A copy
 *     or class edit is a single ripple, not three near-misses that drift.
 *   - Removes the duplicate skeleton helpers from each route file so the
 *     route's own concerns (consent gate, accordion, metadata narrowing,
 *     …) read cleanly without trailing skeleton boilerplate.
 *
 * NOT-FOUND IS NOT ONE OF THE SHARED SHAPES, and used to be. A member
 * whose article is absent now reads the app's own catch-all
 * (`routes/NotFound.tsx`), the same page any unknown address renders,
 * because the distinction this module used to encode was ours and not
 * theirs. The route returns it in place, so nothing here composes it.
 *
 * What this module owns
 * =====================
 *
 *   - The Tailwind class constants used by every article-detail surface.
 *   - The skeleton paragraph count.
 *   - `DetailSurfaceSkeleton` — the shared `aria-busy` placeholder.
 *   - The re-exports of the helper modules' public API
 *     (`DetailSurfaceShell`, `DetailSurfaceShellProps`,
 *     `DetailSurfaceWrapperElement`, `PolicyStatusCard`).
 *
 * What this module does NOT own
 * =============================
 *
 *   - The hero `<img>` block — see `detailSurfaceHero.tsx`.
 *   - The article-surface back-header bar — see
 *     `detailSurfaceBackHeader.tsx`.
 *   - The shell composition (back-header sibling + body wrapper) —
 *     see `detailSurfaceShell.tsx`.
 *   - The `pageClasses` constant itself — see
 *     `detailSurfacePageClasses.ts`, imported directly by the shell.
 *   - The populated branch — each route owns its own composition (consent
 *     gate vs. accordion vs. plain article body); the populated layout
 *     differs intentionally.
 *   - Route-specific Finnish copy (CTA labels, status banner content).
 *     See `copy.ts`'s file header for the inline-vs-shared policy. The
 *     insurance-number label ("Vakuutusnumero") is owned by
 *     `insuranceLabel.ts` and consumed by `PolicyStatusCard` because
 *     the label is byte-identical across every consumer.
 *
 * Spec references
 * ===============
 *
 *   - `specs/003-content-and-knowledge/tasks.md` TA19 / TA20.x.
 *   - `specs/011-pwa-prototype-convergence/spec.md` FR-018 (the
 *     disclosure aside is a render-side concern owned by each route, not
 *     by this module — the watermark is a per-page chrome decision so
 *     the spec wording stays at the call site).
 */

import type { JSX } from 'react';

import { DETAIL_SKELETON_LABEL_COPY } from '../copy.js';
import { DetailSurfaceShell } from './detailSurfaceShell.js';

/**
 * Skeleton placeholder count. Three paragraph bars approximate the shape
 * of a typical Phase A article body without claiming a specific length.
 * Aligned with the populated layout's vertical rhythm so the page does
 * not jump when data lands. Module-local — `DetailSurfaceSkeleton` is the
 * only consumer.
 */
const SKELETON_PARAGRAPH_COUNT: number = 3;

export const headingClasses: string = 'm-0 text-2xl font-bold text-foreground';
// `article-prose` enables the descendant-typography rules in
// `apps/web/src/shell/shell.css` so H2/H3/links/lists inside the
// DOMPurify-sanitised body render with proto-parity hierarchy. Tailwind
// v4 preflight zeroes browser defaults for those elements; without the
// class the body collapses to a plain wall of paragraph text.
export const bodyClasses: string = 'article-prose flex flex-col gap-3 text-base text-foreground';

// Module-local. The four `skeleton*Classes` are consumed only inside
// `DetailSurfaceSkeleton`. Keeping them un-exported pins the public
// surface to the four cross-route layout tokens above + the shell and
// skeleton components.

// Skeleton tokens. `bg-muted` matches the Home deferred-notice tile;
// `animate-pulse` is Tailwind's stock placeholder shimmer. The bars use
// fixed heights (rather than text content) so the skeleton occupies a
// predictable footprint regardless of the device font scale.
const skeletonHeadingClasses: string = 'h-8 w-2/3 animate-pulse rounded-md bg-muted';
const skeletonDisclosureClasses: string = 'h-8 w-full animate-pulse rounded-lg bg-muted';
const skeletonParagraphClasses: string = 'h-4 w-full animate-pulse rounded-md bg-muted';
const skeletonShortParagraphClasses: string = 'h-4 w-5/6 animate-pulse rounded-md bg-muted';

// Hero placeholder. Reserves the same 240px height + outer rhythm
// (`mt-4 mb-8`) the populated `DetailSurfaceHero` block paints, so the
// page does not jump (CLS) when the article query lands. Skeleton-aware
// routes (`ArticleDetailPage`) render this block in their loading branch.
// The five `skeletonHero*` siblings above stay narrow (heading +
// disclosure + paragraphs) — the hero is the only block whose
// populated-vs-loading offset would otherwise differ.
//
// `h-hero-image` is the token-backed utility for the proto's 240px
// (15rem) hero height. Defined in `tokens.css` as
// `--spacing-hero-image: 15rem` so Tailwind generates the utility and
// the no-token-bypass architecture check stays green (FR-047).
const skeletonHeroClasses: string =
  'w-full h-hero-image mb-8 mt-4 animate-pulse rounded-lg bg-muted';

export type {
  DetailSurfaceShellProps,
  DetailSurfaceWrapperElement,
} from './detailSurfaceShell.js';
// Re-export the shell + its props/types so consumers see a single
// entry-point surface (`articleDetailSurface.js`) even though the
// shell has its own module. The split is intentional: the shell
// owns the cross-state composition contract (back-header sibling +
// body wrapping element) which is a different concern from the
// surface tokens / skeleton / insurance-row helpers kept in this file.
export { DetailSurfaceShell } from './detailSurfaceShell.js';

export interface DetailSurfaceSkeletonProps {
  /**
   * `data-testid` to attach to the rendered placeholder. Each consuming
   * route keeps its own testid for human-debugging while sharing the
   * visual structure / a11y attributes; the testid is the only piece of
   * markup that varies across consumers.
   */
  readonly testId: string;
}

/**
 * Loading-state skeleton for an article-detail surface. Mirrors the
 * populated layout's rhythm so the page does not jump on data arrival:
 * heading bar, disclosure bar, then `SKELETON_PARAGRAPH_COUNT` paragraph
 * bars (the last one shorter, to read like the tail of a paragraph
 * rather than a perfectly rectangular block).
 *
 * Carries `aria-busy="true"`, `role="status"`, and `aria-label` so
 * assistive tech announces the in-flight state without reading the
 * placeholder bars themselves (they are decorative and `aria-hidden`).
 */
export function DetailSurfaceSkeleton({ testId }: DetailSurfaceSkeletonProps): JSX.Element {
  // Build the paragraph bars deterministically. `Array.from` over a
  // mutating `for` loop: the count is a constant + the index is the
  // only variable, so a declarative form reads cleanly and avoids the
  // intermediate `paragraphBars: JSX.Element[]` declaration.
  //
  // The bars are decorative `aria-hidden` placeholders with a fixed
  // length (`SKELETON_PARAGRAPH_COUNT`), no reordering, no insertion,
  // no removal — `index` IS the stable identity for each bar. The
  // suppression below is the deliberate, narrow exception that React's
  // index-key guidance specifically calls out (decorative, fixed-length,
  // never-reordered list).
  const paragraphBars = Array.from({ length: SKELETON_PARAGRAPH_COUNT }, (_, index) => {
    const isLast = index === SKELETON_PARAGRAPH_COUNT - 1;
    return (
      <div
        // biome-ignore lint/suspicious/noArrayIndexKey: decorative fixed-length placeholder; index is the stable identity
        key={index}
        aria-hidden="true"
        className={isLast ? skeletonShortParagraphClasses : skeletonParagraphClasses}
      />
    );
  });

  // The back-header is rendered (by `DetailSurfaceShell`) as a sibling
  // of the `role="status"` wrapper rather than inside it: the bar is
  // navigation chrome, not part of the loading region's accessible
  // name. Wrapping it under `aria-busy="true"` would cause AT to wait
  // on the bar before announcing the loading state.
  return (
    <DetailSurfaceShell
      as="div"
      role="status"
      ariaBusy
      ariaLabel={DETAIL_SKELETON_LABEL_COPY}
      testId={testId}
    >
      {/*
       * Hero placeholder. Reserves the exact 240px height the populated
       * hero would render, so the page does not jump on data arrival
       * (CLS). For the 3 articles whose `imageUrl` is null, the
       * populated `DetailSurfaceHero` returns nothing and the layout
       * collapses by the same amount the skeleton occupied — a small
       * one-time shift on the first paint of those routes, judged
       * acceptable against the alternative of either rendering a
       * placeholder card on every loaded article (visually wrong for
       * the 3 hero-less ones) or knowing the slug-to-`imageUrl` map at
       * skeleton-render time (defeats the seam).
       */}
      <div aria-hidden="true" className={skeletonHeroClasses} />
      <div aria-hidden="true" className={skeletonHeadingClasses} />
      <div aria-hidden="true" className={skeletonDisclosureClasses} />
      <div className={bodyClasses}>{paragraphBars}</div>
    </DetailSurfaceShell>
  );
}

// `PolicyStatusCard` is the proto-aligned single-visual-unit version
// of the status banner + insurance-number row. It lives in its own
// file (`PolicyStatusCard.tsx`) so the surface chrome here stays
// under the FR-045 line-count ceiling. Re-export so consumers can
// continue to pull from `./articleDetailSurface.js`.
export { PolicyStatusCard, type PolicyStatusCardProps } from './PolicyStatusCard.js';
